import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatMoney, parseMoney, stripCommas } from '../../utils/moneyUtils';

/**
 * MoneyInput - Controlled input component for currency values
 *
 * Features:
 * - Shows formatted value with commas when not focused (e.g., 1,234.56)
 * - Shows plain number when focused for easy editing (e.g., 1234.56)
 * - Strips commas if user types them
 * - Validates numeric input only (digits, decimal point, minus sign)
 * - Returns parsed float value via onChange
 * - Limits to 2 decimal places
 *
 * @component
 * @example
 * <MoneyInput
 *   value={1234.56}
 *   onChange={(parsedValue) => setAmount(parsedValue)}
 *   placeholder="0.00"
 * />
 */
const MoneyInput = ({
  value,
  onChange,
  onBlur,
  placeholder = '0.00',
  min = 0,
  max,
  disabled = false,
  className = '',
  ...rest
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Update display value when prop value changes
  useEffect(() => {
    if (!isFocused) {
      // When not focused, show formatted value
      if (value !== null && value !== undefined && value !== '') {
        setDisplayValue(formatMoney(value));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused]);

  const handleFocus = (e) => {
    setIsFocused(true);
    // Show plain number for editing
    if (value !== null && value !== undefined && value !== '') {
      setDisplayValue(value.toString());
    } else {
      setDisplayValue('');
    }

    // Select all text on focus for easy replacement
    e.target.select();
  };

  const handleBlur = (e) => {
    setIsFocused(false);

    // Format the display value
    if (value !== null && value !== undefined && value !== '') {
      setDisplayValue(formatMoney(value));
    } else {
      setDisplayValue('');
    }

    // Call parent onBlur if provided
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    let input = e.target.value;

    // Strip commas if user types them
    input = stripCommas(input);

    // Allow empty string
    if (input === '') {
      setDisplayValue('');
      onChange(null);
      return;
    }

    // Validate input: allow digits, one decimal point, minus sign at start
    // This regex allows partial inputs like "12.", "-", "12.5", etc.
    const validInputRegex = /^-?\d*\.?\d{0,2}$/;

    if (!validInputRegex.test(input)) {
      // Invalid input, don't update
      return;
    }

    // Update display value
    setDisplayValue(input);

    // Parse and send to parent
    const parsed = parseMoney(input);

    // Validate against min/max if provided
    if (parsed !== null) {
      if (min !== undefined && parsed < min) {
        // Allow typing but don't call onChange with invalid value
        // This allows user to type "-" or partial negative numbers
        return;
      }
      if (max !== undefined && parsed > max) {
        return;
      }
    }

    onChange(parsed);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      {...rest}
    />
  );
};

MoneyInput.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

export default MoneyInput;
